import { useState } from "react";
import Chart from "react-apexcharts";
import { FiTrendingUp, FiDollarSign, FiShoppingBag, FiStar, FiPackage } from "react-icons/fi";
import useMerchantStats from "../../../Routes/Marketplace/libs/useMerchantStats";
import StexSelect from "../../../Routes/Marketplace/components/StexSelect";
import "../../../Routes/Marketplace/components/StexSelect.css";
import PageHeader from "../../components/PageHeader";
import StatCard from "../../components/StatCard";
import DataTable from "../../components/DataTable";
import { formatNgn } from "../../../Routes/Marketplace/libs/orderDisplayUtils";

const PERIOD_OPTIONS = [
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
];

const MerchantAnalyticsPage = () => {
  const [days, setDays] = useState(30);
  const { data, isLoading, isError } = useMerchantStats(days);

  const chartOptions = {
    chart: { toolbar: { show: false } },
    xaxis: { categories: data?.dailyRevenue?.map((d) => d.date.slice(5)) || [] },
    colors: ["#284697"],
    stroke: { curve: "smooth" },
  };
  const chartSeries = [{ name: "Revenue (NGN)", data: data?.dailyRevenue?.map((d) => d.amount) || [] }];

  const topProductColumns = [
    { key: "name", label: "Product" },
    { key: "units", label: "Units sold" },
    {
      key: "revenue",
      label: "Revenue",
      render: (row) => formatNgn(row.revenue),
    },
  ];

  return (
    <>
      <PageHeader
        title="Analytics"
        subtitle="Sales performance and product insights"
        icon={FiTrendingUp}
        actions={
          <div className="dashboard-analytics-period">
            <StexSelect
              name="analyticsPeriod"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              options={PERIOD_OPTIONS}
              placeholder="Select period"
            />
          </div>
        }
      />
      {isError && <p style={{ color: "var(--dash-danger)" }}>Failed to load analytics.</p>}
      <div className="dashboard-stat-grid">
        <StatCard
          label="Revenue (period)"
          value={isLoading ? "…" : formatNgn(data?.totals?.sales || 0)}
          icon={FiDollarSign}
        />
        <StatCard label="Paid orders" value={isLoading ? "…" : data?.totals?.orders ?? 0} icon={FiShoppingBag} />
        <StatCard label="Units sold" value={isLoading ? "…" : data?.totals?.unitsSold ?? 0} icon={FiPackage} />
        <StatCard
          label="Avg rating"
          value={
            isLoading
              ? "…"
              : data?.reviewSummary?.count
                ? `${data.reviewSummary.avgRating} ★ (${data.reviewSummary.count})`
                : "No reviews"
          }
          icon={FiStar}
        />
      </div>
      <div className="dashboard-card">
        <h2 className="dashboard-card__title">Revenue trend</h2>
        {isLoading ? (
          <p>Loading chart…</p>
        ) : chartSeries[0].data.some((v) => v > 0) ? (
          <Chart options={chartOptions} series={chartSeries} type="area" height={320} />
        ) : (
          <p style={{ color: "var(--dash-muted)" }}>No revenue data for this period.</p>
        )}
      </div>
      <div className="dashboard-card" style={{ marginTop: 24 }}>
        <h2 className="dashboard-card__title">Top products</h2>
        <p className="dashboard-form-hint">Best sellers by revenue in the selected period.</p>
        <DataTable
          columns={topProductColumns}
          rows={(data?.topProducts || []).map((row, index) => ({ ...row, _key: row.productId || index }))}
          loading={isLoading}
          emptyMessage="No paid orders in this period yet."
        />
      </div>
    </>
  );
};

export default MerchantAnalyticsPage;
